import { getUserPreferences, getUser } from '@go-client/lib/api/user';
import { UserPreferences, User } from '@go-shared/types/user';
import React, { useContext, useEffect, useState } from 'react';
import { BubbleOverlay } from '@go-client/components/BubbleUI';

interface RefreshUserInfoContextOptions {
  skipUser?: boolean;
  skipPreferences?: boolean;
}

export interface UserInfoBundle {
  user: User;
  preferences: UserPreferences;
  refreshContext: (options?: RefreshUserInfoContextOptions) => Promise<void>;
}

const UserInfoContext = React.createContext<UserInfoBundle | undefined>(undefined);

export function useUserInfo() {
  const context = useContext(UserInfoContext);

  if (context === undefined) {
    throw new Error('useUserInfo must be used within a UserInfoProvider');
  }

  return context;
}

export function WithUserInfo(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className: propClasses, children, ...rest } = props;

  // WARNING: This state cannot be changed by anyone other than 'fetchUserInfo'.
  // This helps keep the context predictable, with only one way of refreshing the context.
  // Please do not expose any other method of modifying state.
  const [userInfoBundle, setUserInfoBundle] = useState<UserInfoBundle | undefined>(undefined);

  async function fetchUserInfo(
    prevUserInfoBundle?: Omit<UserInfoBundle, 'refreshContext'>,
    options?: RefreshUserInfoContextOptions
  ) {
    const user =
      options && options.skipUser && prevUserInfoBundle ? prevUserInfoBundle.user : await getUser();
    const preferences =
      options && options.skipPreferences && prevUserInfoBundle
        ? prevUserInfoBundle.preferences
        : await getUserPreferences();

    setUserInfoBundle({
      user,
      preferences,
      refreshContext: (opts?: RefreshUserInfoContextOptions) =>
        fetchUserInfo({ user, preferences }, opts),
    });
  }

  useEffect(() => {
    fetchUserInfo().catch(console.error);
  }, []);

  const classes = ['h-full'];
  if (propClasses) classes.push(propClasses);

  return (
    <BubbleOverlay loading={!userInfoBundle} {...rest} className={classes.join(' ')}>
      {userInfoBundle && (
        <UserInfoProvider userInfoBundle={userInfoBundle}>{props.children}</UserInfoProvider>
      )}
    </BubbleOverlay>
  );
}

interface UserInfoProviderPropsBase {
  userInfoBundle: UserInfoBundle;
}

type UserInfoProviderProps = UserInfoProviderPropsBase & { children?: React.ReactNode };

export default function UserInfoProvider(props: UserInfoProviderProps) {
  const { userInfoBundle, children } = props;

  return <UserInfoContext.Provider value={userInfoBundle}>{children}</UserInfoContext.Provider>;
}
